import React, { useEffect, useRef, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Radio,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

import {
  type MarketingRouterTreatmentType,
  getMarketingRouterTreatmentConfig,
  marketingRouterTreatmentTypes,
} from '@luxuryescapes/contract-svc-promo/marketing-router/admin';

import type { MarketingRouterRule } from '~/types/marketingRouterRule';

import MarketingRouterTermLabel from './MarketingRouterTermLabel';
import {
  type MarketingRouterTreatmentDraft,
  type MarketingRouterTreatmentFieldName,
  getTreatmentFieldValueForDraft,
} from './marketingRouterTreatments';

type Variant = Omit<MarketingRouterRule['variants'][number], 'treatments'> & {
  treatments?: Array<MarketingRouterTreatmentDraft>;
};

export type MarketingRouterVariantCardProps = {
  variant: Variant;
  index: number;
  cookieName: string;
  canRemove: boolean;
  showFieldDescriptions: boolean;
  onFieldChange: (field: 'variant_name' | 'weight', value: string) => void;
  onMakeControl: () => void;
  onRemove: () => void;
  onAddTreatment: () => void;
  onTreatmentTypeChange: (treatmentIndex: number, type: MarketingRouterTreatmentType) => void;
  onTreatmentFieldChange: (treatmentIndex: number, field: MarketingRouterTreatmentFieldName, value: string) => void;
  onRemoveTreatment: (treatmentIndex: number) => void;
};

/** What a treatment does, in one line: its type, then the fields that are set. */
export function getTreatmentSummary(treatment: MarketingRouterTreatmentDraft): string {
  const config = getMarketingRouterTreatmentConfig(treatment.type);
  const fields =
    config?.fields
      ?.map((field) => {
        const value = getTreatmentFieldValueForDraft(treatment, field.field);
        return value ? `${field.fieldName}: ${value}` : null;
      })
      .filter((field): field is string => field != null) ?? [];

  return fields.length
    ? `${config?.typeName ?? treatment.type} — ${fields.join(', ')}`
    : (config?.typeName ?? treatment.type);
}

/**
 * One variant: its identity and split on the header, its treatments below.
 *
 * A treatment reads as a summary line until it is opened, so a variant fits on
 * one screen and the fields on show are the ones being edited. A treatment
 * that has just been added opens on its own — it is empty, so its summary
 * would say nothing.
 */
export default function MarketingRouterVariantCard({
  variant,
  index,
  cookieName,
  canRemove,
  showFieldDescriptions,
  onFieldChange,
  onMakeControl,
  onRemove,
  onAddTreatment,
  onTreatmentTypeChange,
  onTreatmentFieldChange,
  onRemoveTreatment,
}: MarketingRouterVariantCardProps) {
  const treatments = variant.treatments ?? [];
  const [expandedTreatmentIndices, setExpandedTreatmentIndices] = useState<Array<number>>([]);
  const previousTreatmentCount = useRef(treatments.length);

  useEffect(() => {
    if (treatments.length > previousTreatmentCount.current) {
      setExpandedTreatmentIndices((current) => [...current, treatments.length - 1]);
    }
    previousTreatmentCount.current = treatments.length;
  }, [treatments.length]);

  const toggleTreatment = (treatmentIndex: number) => {
    setExpandedTreatmentIndices((current) =>
      current.includes(treatmentIndex)
        ? current.filter((expanded) => expanded !== treatmentIndex)
        : [...current, treatmentIndex],
    );
  };

  return (
    <Card variant="outlined">
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'action.hover',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="subtitle2">Variant {index + 1}</Typography>
        {variant.is_control ? <Chip label="Control" size="small" color="primary" variant="outlined" /> : null}
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {variant.weight}% of traffic
        </Typography>
        <Tooltip title={canRemove ? 'Remove variant' : 'A rule needs at least one variant'}>
          <span>
            <IconButton
              size="small"
              aria-label={`Remove variant ${index + 1}`}
              onClick={onRemove}
              disabled={!canRemove}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <TextField
              label="Variant name"
              required
              value={variant.variant_name}
              onChange={(event) => onFieldChange('variant_name', event.target.value)}
              sx={{ flex: '1 1 260px' }}
            />
            <TextField
              label={<MarketingRouterTermLabel term="weight">Weight</MarketingRouterTermLabel>}
              required
              type="number"
              inputProps={{ min: 0, max: 100, step: 1 }}
              InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
              value={variant.weight}
              onChange={(event) => onFieldChange('weight', event.target.value)}
              sx={{ flex: '0 0 140px' }}
            />
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: '0 0 auto' }}>
              <Radio
                checked={variant.is_control}
                onChange={onMakeControl}
                inputProps={{ 'aria-label': `Make variant ${index + 1} the control` }}
              />
              <Typography variant="body2">Control</Typography>
            </Stack>
          </Stack>

          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            Cookie: {cookieName}
          </Typography>

          <Divider />

          <Stack spacing={1}>
            <Typography variant="subtitle2">Treatments</Typography>
            {treatments.length ? (
              treatments.map((treatment, treatmentIndex) => {
                const config = getMarketingRouterTreatmentConfig(treatment.type);
                const isExpanded = expandedTreatmentIndices.includes(treatmentIndex);

                return (
                  <Paper key={`${treatment.type}-${treatmentIndex}`} variant="outlined">
                    <Box
                      component="button"
                      type="button"
                      onClick={() => toggleTreatment(treatmentIndex)}
                      aria-expanded={isExpanded}
                      sx={{
                        alignItems: 'center',
                        background: 'none',
                        border: 0,
                        cursor: 'pointer',
                        display: 'flex',
                        font: 'inherit',
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        textAlign: 'left',
                        width: '100%',
                      }}
                    >
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {getTreatmentSummary(treatment)}
                      </Typography>
                      <ExpandMoreIcon
                        fontSize="small"
                        sx={{
                          color: 'action.active',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: (theme) =>
                            theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
                        }}
                      />
                    </Box>
                    <Collapse in={isExpanded} unmountOnExit>
                      <Divider />
                      <Stack spacing={1} sx={{ p: 2 }}>
                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                          <TextField
                            label="Type"
                            select
                            value={treatment.type}
                            sx={{ flex: '1 1 220px' }}
                            onChange={(event) => {
                              const nextType = marketingRouterTreatmentTypes.find(
                                (type) => type === event.target.value,
                              );
                              if (nextType) {
                                onTreatmentTypeChange(treatmentIndex, nextType);
                              }
                            }}
                          >
                            {marketingRouterTreatmentTypes.map((type) => (
                              <MenuItem key={type} value={type}>
                                {getMarketingRouterTreatmentConfig(type).typeName}
                              </MenuItem>
                            ))}
                          </TextField>
                          {config?.fields?.map((field) => (
                            <TextField
                              key={field.field}
                              label={field.required ? field.fieldName : `${field.fieldName} (optional)`}
                              required={field.required}
                              helperText={showFieldDescriptions ? field.longDescription : undefined}
                              value={getTreatmentFieldValueForDraft(treatment, field.field)}
                              sx={{ flex: '1 1 220px' }}
                              onChange={(event) =>
                                onTreatmentFieldChange(treatmentIndex, field.field, event.target.value)
                              }
                            />
                          ))}
                          <Button
                            type="button"
                            color="error"
                            onClick={() => onRemoveTreatment(treatmentIndex)}
                            sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                          >
                            Remove
                          </Button>
                        </Stack>
                        {showFieldDescriptions && config?.longDescription ? (
                          <Typography color="text.secondary" variant="body2">
                            {config.longDescription}
                          </Typography>
                        ) : null}
                      </Stack>
                    </Collapse>
                  </Paper>
                );
              })
            ) : (
              <Typography color="text.secondary" variant="body2">
                No treatments configured — this variant shows the visitor nothing.
              </Typography>
            )}
            <Box>
              <Button type="button" size="small" onClick={onAddTreatment}>
                Add treatment
              </Button>
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
